import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBookmark,
  IconBuildingFactory2,
  IconCircleCheck,
  IconGlobe,
  IconUsers,
} from "@tabler/icons-react";
import CompanyProfileSurfaceLink from "@/components/shared/CompanyProfileSurfaceLink";
import TableRowLinkTableStyles from "@/components/shared/TableRowLinkTableStyles";
import ShortlistModal from "@/components/shared/UserSignupModal";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import {
  getCollaborationIntentLabel,
  getIndustryLabel,
  regionsData,
} from "@/components/shared/constants/general.const";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import {
  buildCompanyDiscoverPath,
  buildLoggedInCompanyProfilePath,
  companyProfileAriaLabel,
} from "@/shared/utils/companyProfileLink.util";
import { useAuth } from "@/hooks/useAuth";

const CARD_BORDER = "1px solid #E2E8F0";
const LOOKING_FOR_BG = "#F0FDF4";
const LOOKING_FOR_ACCENT = "#16A34A";

const DISCOVER_BOOKMARK_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
} as const;

const VIEW_COMPANY_BTN_STYLES = {
  root: {
    borderColor: "#04305c",
    color: "#04305c",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#04305c",
    },
  },
} as const;

function getRegionLabel(regionId: string): string {
  return regionsData.regions.find((region) => region.id === regionId)?.name ?? regionId;
}

type FirstWithMore = { first: string; remaining: number };

function firstWithMore(labels: string[]): FirstWithMore {
  if (labels.length === 0) return { first: "—", remaining: 0 };
  return { first: labels[0], remaining: Math.max(0, labels.length - 1) };
}

function formatOperatingGeography(company: CompanyWithUI): FirstWithMore {
  const operating = company.operatingCountries ?? [];
  const labels = operating.map((regionId) => getRegionLabel(regionId));
  return firstWithMore(labels);
}

function formatIndustryList(industries: string[]): FirstWithMore {
  const labels = industries.map((cat) => getIndustryLabel(cat));
  return firstWithMore(labels);
}

function formatCollaborationIntentMeta(company: CompanyWithUI): string {
  const intents = company.collaborationIntent ?? [];
  if (intents.length === 0) return "—";
  return intents.map((intent) => getCollaborationIntentLabel(intent)).join(", ");
}

function getLookingForItems(company: CompanyWithUI): string[] {
  const intents = company.collaborationIntent ?? [];
  if (intents.length === 0) return [];
  return intents.map((intent) => getCollaborationIntentLabel(intent));
}

export type DiscoverCompanyCardProps = {
  company: CompanyWithUI;
  profileSearch?: string;
  discoverReturnTo?: string;
  loggedInProfilePathBuilder?: (companyId: string) => string;
  canViewCompanyDetails?: boolean;
  showShortlistButton?: boolean;
  secondaryColumn?: "industry" | "collaborationIntent";
  /** Denser layout for 5-column guest discover grid. */
  compact?: boolean;
  /** Hide the company description/title line (e.g. sector featured preview). */
  hideTitle?: boolean;
  /** Hide the industry meta line. */
  hideIndustry?: boolean;
  onViewDetails: (companyId: string) => void;
  onShortlistClick: (companyId: string, isShortlisted: boolean) => void | Promise<void>;
  onGuestShortlistClick?: (companyId: string) => void;
  onGuestShortlistRemove?: (companyId: string) => void;
};

export default function DiscoverCompanyCard({
  company,
  profileSearch = "",
  discoverReturnTo,
  loggedInProfilePathBuilder,
  canViewCompanyDetails = true,
  showShortlistButton = true,
  secondaryColumn = "industry",
  compact = false,
  hideTitle = false,
  hideIndustry = false,
  onViewDetails,
  onShortlistClick,
  onGuestShortlistClick,
  onGuestShortlistRemove,
}: DiscoverCompanyCardProps) {
  const { isAuthenticated } = useAuth();
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const companyLabel = company.gtid || "N/A";
  const flag = flagEmojiFromHeadquartersCountry(company.headquartersCountry);
  const rawDescription = company.subDescription?.trim() || company.description;
  const title = formatCompanyProfileTextForDisplay(rawDescription) || companyLabel;
  const industryLabel = formatIndustryList(
    Array.isArray(company.industryCategory) ? company.industryCategory : [],
  );
  const lookingForItems = getLookingForItems(company);
  const isVerified = company.status === "VERIFIED" || company.status === "SYSTEM_VERIFIED";

  const profilePath = canViewCompanyDetails
    ? isAuthenticated
      ? loggedInProfilePathBuilder
        ? loggedInProfilePathBuilder(company.id)
        : buildLoggedInCompanyProfilePath(company.id)
      : buildCompanyDiscoverPath(company.id, profileSearch)
    : undefined;

  const handleShortlist = () => {
    if (!isAuthenticated) {
      if (company.isShortlisted) {
        onGuestShortlistRemove?.(company.id);
        return;
      }
      onGuestShortlistClick?.(company.id);
      setSignupModalOpen(true);
      return;
    }
    void onShortlistClick(company.id, !company.isShortlisted);
  };

  const operatingGeography = formatOperatingGeography(company);
  const secondaryMetaValue =
    secondaryColumn === "collaborationIntent" ? formatCollaborationIntentMeta(company) : null;
  const lookingForLimit = 2;
  const cardPadding = compact ? "sm" : "md";
  const stackGap = compact ? "xs" : "sm";

  return (
    <>
      <TableRowLinkTableStyles />
      <Card
        radius="lg"
        p={cardPadding}
        h="100%"
        onMouseEnter={compact ? () => setIsHovered(true) : undefined}
        onMouseLeave={compact ? () => setIsHovered(false) : undefined}
        style={{
          border: CARD_BORDER,
          backgroundColor: "#FFFFFF",
          boxShadow:
            compact && isHovered
              ? "0 16px 40px rgba(15, 23, 42, 0.18)"
              : compact
                ? "0 4px 16px rgba(15, 23, 42, 0.06)"
                : "0 1px 2px rgba(15, 23, 42, 0.04)",
          position: "relative",
          transition: "transform 200ms ease, box-shadow 200ms ease",
          transform: compact && isHovered ? "scale(1.000001)" : "scale(1)",
          transformOrigin: "center",
          zIndex: compact && isHovered ? 5 : undefined,
        }}
      >
        {profilePath ? (
          <CompanyProfileSurfaceLink
            to={profilePath}
            linkState={discoverReturnTo ? { discoverReturnTo } : undefined}
            ariaLabel={companyProfileAriaLabel(company.gtid, companyLabel)}
          />
        ) : null}

        <Stack
          gap={stackGap}
          h="100%"
          style={{ pointerEvents: "none", position: "relative", zIndex: 2 }}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap={compact ? 4 : "sm"}>
            <Stack gap={compact ? 4 : 6} style={{ minWidth: 0 }}>
              <Text fw={700} size={compact ? "xs" : "sm"} c="#04305c" lh={1.2}>
                {companyLabel}
              </Text>
              {flag ? (
                <Text
                  component="span"
                  fz={compact ? 16 : 20}
                  lh={1}
                  role="img"
                  aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                >
                  {flag}
                </Text>
              ) : null}
            </Stack>

            <Group
              gap={compact ? 4 : 8}
              wrap="nowrap"
              style={{ flexShrink: 0 }}
              data-surface-action
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {isVerified ? (
                <Group
                  gap={compact ? 4 : 6}
                  wrap="nowrap"
                  px={compact ? 8 : 10}
                  py={compact ? 3 : 4}
                  style={{
                    borderRadius: 999,
                    backgroundColor: "#ECFDF5",
                  }}
                >
                  <ThemeIcon size={compact ? 14 : 16} radius="xl" color="green" variant="filled">
                    <IconCircleCheck size={compact ? 9 : 10} />
                  </ThemeIcon>
                  <Text size={compact ? "10px" : "xs"} fw={700} c={LOOKING_FOR_ACCENT} tt="uppercase" lh={1}>
                    Verified
                  </Text>
                </Group>
              ) : null}
              {showShortlistButton ? (
                <Button
                  type="button"
                  variant="filled"
                  size="compact-sm"
                  radius="md"
                  px="xs"
                  styles={DISCOVER_BOOKMARK_BTN_STYLES}
                  aria-label={company.isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleShortlist();
                  }}
                  style={{ pointerEvents: "auto", position: "relative", zIndex: 3 }}
                >
                  <IconBookmark
                    size={16}
                    color="white"
                    fill={company.isShortlisted ? "currentColor" : "none"}
                  />
                </Button>
              ) : null}
            </Group>
          </Group>

          <Stack gap={compact ? 4 : 6} style={{ flex: 1, minHeight: 0 }}>
            {hideTitle ? null : (
              <Text
                fw={700}
                size={compact ? "sm" : "md"}
                c="#0F172A"
                lh={1.3}
                style={{ wordBreak: "break-word" }}
              >
                {title}
              </Text>
            )}

            {hideIndustry ? null : (
              <Group gap={6} wrap="nowrap" align="flex-start">
                <IconBuildingFactory2 size={compact ? 14 : 16} color="#64748B" style={{ flexShrink: 0, marginTop: 2 }} />
                <Text
                  size={compact ? "xs" : "sm"}
                  c="dimmed"
                  style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
                >
                  {industryLabel.first}
                  {industryLabel.remaining > 0 ? (
                    <Text component="span" c="#04305c" fw={600}>
                      {` +${industryLabel.remaining} more`}
                    </Text>
                  ) : null}
                </Text>
              </Group>
            )}

            {lookingForItems.length > 0 ? (
              <Box
                p={compact ? "xs" : "sm"}
                style={{
                  borderRadius: compact ? 10 : 12,
                  backgroundColor: LOOKING_FOR_BG,
                }}
              >
                <Text
                  size={compact ? "10px" : "xs"}
                  fw={700}
                  c={LOOKING_FOR_ACCENT}
                  tt="uppercase"
                  mb={compact ? 4 : 6}
                  lh={1}
                >
                  Looking For
                </Text>
                <Stack gap={compact ? 4 : 6}>
                  {lookingForItems.slice(0, lookingForLimit).map((item) => (
                    <Group key={item} gap={compact ? 6 : 8} wrap="nowrap" align="flex-start">
                      <ThemeIcon
                        size={compact ? 14 : 18}
                        radius="xl"
                        color="green"
                        variant="filled"
                        style={{ flexShrink: 0 }}
                      >
                        <IconCircleCheck size={compact ? 8 : 11} />
                      </ThemeIcon>
                      <Text
                        size={compact ? "xs" : "sm"}
                        c="#0F172A"
                        lh={1.3}
                        style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
                      >
                        {item}
                      </Text>
                    </Group>
                  ))}
                  {lookingForItems.length > lookingForLimit ? (
                    <Text size={compact ? "10px" : "xs"} fw={600} c={LOOKING_FOR_ACCENT}>
                      +{lookingForItems.length - lookingForLimit} more
                    </Text>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
          </Stack>

          <Group gap={6} wrap="wrap" align="center">
            <IconGlobe size={compact ? 12 : 14} color="#94A3B8" style={{ flexShrink: 0 }} />
            <Text size={compact ? "10px" : "xs"} c="dimmed" style={{ flexShrink: 0 }}>
              Operating Geography
            </Text>
            <Text
              size={compact ? "xs" : "sm"}
              fw={700}
              c="#04305c"
              style={{ wordBreak: "break-word" }}
            >
              {operatingGeography.first}
              {operatingGeography.remaining > 0 ? (
                <Text component="span" c="#04305c" fw={600}>
                  {` +${operatingGeography.remaining} more`}
                </Text>
              ) : null}
            </Text>
          </Group>

          {secondaryColumn === "collaborationIntent" && secondaryMetaValue ? (
            <Group gap={6} wrap="wrap" align="center">
              <IconUsers size={compact ? 12 : 14} color="#94A3B8" style={{ flexShrink: 0 }} />
              <Text size={compact ? "10px" : "xs"} c="dimmed" style={{ flexShrink: 0 }}>
                Collaboration Model
              </Text>
              <Text
                size={compact ? "xs" : "sm"}
                fw={700}
                c="#04305c"
                style={{ wordBreak: "break-word" }}
              >
                {secondaryMetaValue}
              </Text>
            </Group>
          ) : null}

          <Flex
            data-surface-action
            style={{ pointerEvents: "auto", position: "relative", zIndex: 2 }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              fullWidth
              radius="md"
              size={compact ? "compact-sm" : "sm"}
              styles={VIEW_COMPANY_BTN_STYLES}
              rightSection={<IconArrowRight size={compact ? 14 : 16} />}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewDetails(company.id);
              }}
            >
              {compact ? "View Opportunity" : "View Company"}
            </Button>
          </Flex>
        </Stack>
      </Card>

      <ShortlistModal
        opened={signupModalOpen}
        onClose={() => setSignupModalOpen(false)}
        companyName={company.gtid ? `${company.gtid}` : (company.legalName ?? "this company")}
      />
    </>
  );
}
